import { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, Outlet, useLocation, useMatch, useNavigate, useSearchParams } from 'react-router-dom';
import type { ThemePreference } from '@clearflow/shared';
import { api } from './lib/api.ts';
import { SidebarProvider } from './contexts/SidebarContext.tsx';
import { RoleBadge } from './components/RoleBadge.tsx';
import { WhatsNewTrigger } from './components/WhatsNewDrawer.tsx';

// Modale volumineuse (~1000 lignes) ouverte à la demande : chargée à la première ouverture plutôt que
// dans le bundle initial (GU-358). Pas de fallback visible : le chunk est petit et la modale apparaît dès
// qu'il est arrivé.
const SettingsModal = lazy(() => import('./components/SettingsModal.tsx').then((m) => ({ default: m.SettingsModal })));

function SettingsIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" width="14" height="14">
      <circle cx="8" cy="8" r="2.3" stroke="currentColor" strokeWidth="1.3" />
      <path
        d="M8 1.6v1.3M8 13.1v1.3M14.4 8h-1.3M2.9 8H1.6M12.4 3.6l-.9.9M4.5 11.5l-.9.9M12.4 12.4l-.9-.9M4.5 4.5l-.9-.9"
        stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"
      />
    </svg>
  );
}

function AuditLogIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" width="14" height="14">
      <path d="M8 4.2v4.1l2.6 1.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="8" cy="8" r="6.2" stroke="currentColor" strokeWidth="1.3" />
    </svg>
  );
}

function MonitoringIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" width="14" height="14">
      <path d="M2 13V3M2 13h12" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4.5 10.5 7 7.5 9 9.5 12.5 5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" width="14" height="14">
      <path d="M6.5 2.5H3.5a1 1 0 0 0-1 1v9a1 1 0 0 0 1 1h3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M10.5 5.2 L13.3 8 L10.5 10.8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M13 8H6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

// Icône affichée = thème vers lequel le clic bascule (soleil en dark → "passer au clair", lune
// en light → "passer au sombre"), pas le thème courant — convention la plus répandue (GitHub,
// VS Code) pour un bouton à un seul état visuel plutôt qu'un menu.
function SunIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" width="14" height="14">
      <circle cx="8" cy="8" r="3" stroke="currentColor" strokeWidth="1.3" />
      <path
        d="M8 1.4v1.5M8 13.1v1.5M14.6 8h-1.5M2.9 8H1.4M12.7 3.3l-1.1 1.1M4.4 11.6l-1.1 1.1M12.7 12.7l-1.1-1.1M4.4 4.4l-1.1-1.1"
        stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"
      />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" width="14" height="14">
      <path
        d="M13.8 9.9A5.8 5.8 0 1 1 6.1 2.2a4.6 4.6 0 0 0 7.7 7.7Z"
        stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"
      />
    </svg>
  );
}

// Placé dans le header (à côté de WhatsNewTrigger) plutôt que dans SettingsModal : un seul clic
// pour basculer, pas de "Enregistrer" à part. ['preferences'] est la même clé de cache que
// WhatsNewTrigger/RepoViewPage — déjà préchauffée par AppInner, donc pas de requête en plus.
function ThemeToggle() {
  const qc = useQueryClient();
  const { data: preferences } = useQuery({
    queryKey: ['preferences'],
    queryFn: () => api.getPreferences(),
  });
  const theme: ThemePreference = preferences?.theme ?? 'dark';
  const next: ThemePreference = theme === 'dark' ? 'light' : 'dark';

  const toggleMutation = useMutation({
    mutationFn: (patch: { theme: ThemePreference }) => api.updatePreferences(patch),
    onSuccess: (updated) => qc.setQueryData(['preferences'], updated),
  });

  return (
    <button
      className="whats-new-trigger"
      title={theme === 'dark' ? 'Passer au thème clair' : 'Passer au thème sombre'}
      onClick={() => toggleMutation.mutate({ theme: next })}
      disabled={toggleMutation.isPending}
    >
      {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
    </button>
  );
}

function UserMenu({
  name,
  email,
  avatarUrl,
  currentRepoId,
  onLogout,
}: {
  name: string | null;
  email: string;
  avatarUrl: string | null;
  currentRepoId: string | null;
  onLogout: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="user-menu" ref={ref}>
      <button className="user-menu-toggle" onClick={() => setOpen((v) => !v)}>
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt={email}
            style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover' }}
          />
        ) : (
          <span>{email}</span>
        )}
        <span style={{ fontSize: 9, opacity: 0.7 }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="user-menu-dropdown">
          {name && (
            <div className="user-menu-header">
              <span className="user-menu-name">{name}</span>
              <span className="user-menu-email muted">{email}</span>
            </div>
          )}
          <button
            type="button"
            className="user-menu-item"
            onClick={() => {
              setOpen(false);
              navigate(currentRepoId ? `/audit-log?fromRepo=${currentRepoId}` : '/audit-log');
            }}
          >
            <AuditLogIcon />
            <span>Journal d'activité</span>
          </button>
          <button
            type="button"
            className="user-menu-item"
            onClick={() => {
              setOpen(false);
              navigate(currentRepoId ? `/monitoring?fromRepo=${currentRepoId}` : '/monitoring');
            }}
          >
            <MonitoringIcon />
            <span>Monitoring</span>
          </button>
          <button
            type="button"
            className="user-menu-item"
            onClick={() => { setOpen(false); setShowSettings(true); }}
          >
            <SettingsIcon />
            <span>Paramètres</span>
          </button>
          <button
            type="button"
            className="user-menu-item"
            onClick={() => { setOpen(false); onLogout(); }}
          >
            <LogoutIcon />
            <span>Déconnexion</span>
          </button>
        </div>
      )}
      {showSettings && (
        <Suspense fallback={null}>
          <SettingsModal onClose={() => setShowSettings(false)} />
        </Suspense>
      )}
    </div>
  );
}

function AppInner() {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: session, isLoading } = useQuery({
    queryKey: ['session'],
    queryFn: () => api.getSession().catch(() => null),
  });

  // Préchauffe le cache ['preferences'] dès la connexion plutôt que d'attendre que SettingsModal
  // ou RepoViewPage le déclenchent chacun de leur côté à leur premier montage — sans ça, arriver
  // directement sur /repos/:id affiche toutes les sections de sidebar par défaut le temps que la
  // vraie préférence revienne, avant de se corriger (flash visible si l'utilisateur en avait
  // masqué). Aussi utilisé ci-dessous pour rediriger vers le dernier repo ouvert.
  const { data: preferences } = useQuery({
    queryKey: ['preferences'],
    queryFn: () => api.getPreferences(),
    enabled: !!session,
  });

  // Pilote le thème clair/sombre via l'attribut data-theme sur <html> — lu par :root[data-theme=
  // "light"] dans styles.css. ThemeToggle (header, à côté de WhatsNewTrigger) met à jour
  // ['preferences'] via qc.setQueryData au succès du PATCH (même clé de cache), donc ce hook voit
  // la nouvelle valeur sans requête supplémentaire ni prop drilling. 'dark' explicite tant que la
  // préférence n'a pas encore résolu, pour matcher les valeurs par défaut de :root (pas de flash
  // différent du statu quo).
  useEffect(() => {
    document.documentElement.dataset.theme = preferences?.theme ?? 'dark';
  }, [preferences?.theme]);

  const queryClient = useQueryClient();
  const repoMatch = useMatch('/repos/:id');
  const currentRepoId = repoMatch?.params.id ?? null;
  const [searchParams] = useSearchParams();
  // Sur /audit-log ou /monitoring (pages globales, sans repo dans l'URL), propose quand même un
  // 3e segment de breadcrumb vers le repo d'origine — ?fromRepo= posé par UserMenu en y naviguant
  // depuis un repo précis, ou à défaut le dernier repo ouvert en session — pour ne pas perdre le
  // fil d'Ariane en quittant RepoViewPage. currentRepoId reste prioritaire : sur /repos/:id lui-
  // même, c'est bien le repo affiché qui doit peupler le breadcrumb, pas fromRepo/lastRepoId.
  const isGlobalPage = location.pathname === '/audit-log' || location.pathname === '/monitoring';
  const fromRepoId = isGlobalPage ? (searchParams.get('fromRepo') ?? preferences?.lastRepoId ?? null) : null;
  const breadcrumbRepoId = currentRepoId ?? fromRepoId;
  const repoMetaQuery = useQuery({
    queryKey: ['repo-meta', breadcrumbRepoId],
    queryFn: () => api.getRepo(breadcrumbRepoId!),
    enabled: !!breadcrumbRepoId,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (isLoading) return;
    const isLogin = location.pathname.startsWith('/login');
    if (!session && !isLogin) navigate('/login', { replace: true });
    if (session && isLogin) navigate('/repos', { replace: true });
  }, [session, isLoading, location.pathname, navigate]);

  // Redirige une seule fois vers le dernier repo ouvert (RepoViewPage.tsx écrit lastRepoId à
  // chaque repo visité) quand on atterrit sur la liste "/repos" nue — au premier chargement de
  // session (login, ou reprise d'une session existante via /) uniquement : le ref empêche de
  // rejouer ce saut si l'utilisateur clique ensuite lui-même sur "Dépôts" pour revenir à la
  // liste, ce qui le renverrait aussitôt dans le même repo malgré lui. Le ref est consommé dès
  // que les préférences sont chargées, quel que soit le chemin : si l'appli est ouverte ou
  // rechargée directement sur /repos/:id, le premier clic sur "Dépôts" ne doit pas être pris
  // pour un premier chargement et renvoyer aussitôt dans ce repo.
  const didAutoNavigateToLastRepo = useRef(false);
  useEffect(() => {
    if (didAutoNavigateToLastRepo.current || isLoading || !session) return;
    // Juste après une connexion, session existe alors que le chemin est encore /login (l'autre
    // effet redirige vers /repos) : ne pas consommer le ref ici, sinon la redirection est perdue.
    if (location.pathname.startsWith('/login')) return;
    if (preferences === undefined) return; // attend le premier chargement des préférences
    didAutoNavigateToLastRepo.current = true;
    if (location.pathname === '/repos' && preferences.lastRepoId) {
      navigate(`/repos/${preferences.lastRepoId}`, { replace: true });
    }
  }, [session, isLoading, location.pathname, preferences, navigate]);

  return (
    <div className="layout">
      <header className="header">
        <div className="breadcrumb">
          <h1 className="breadcrumb-brand">🔀 Clearflow</h1>
          {session && (
            <>
              <span className="breadcrumb-sep" aria-hidden="true">›</span>
              {location.pathname === '/repos' ? (
                <span className="breadcrumb-current">Dépôts</span>
              ) : (
                <Link to="/repos" className="breadcrumb-link">Dépôts</Link>
              )}
            </>
          )}
          {repoMetaQuery.data && (
            <>
              <span className="breadcrumb-sep" aria-hidden="true">›</span>
              {currentRepoId ? (
                <span className="header-repo-name breadcrumb-current" title={repoMetaQuery.data.pathWithNamespace}>
                  {repoMetaQuery.data.name}
                </span>
              ) : (
                <Link
                  to={`/repos/${breadcrumbRepoId}`}
                  className="header-repo-name breadcrumb-link"
                  title={repoMetaQuery.data.pathWithNamespace}
                >
                  {repoMetaQuery.data.name}
                </Link>
              )}
            </>
          )}
        </div>
        <div className="row" style={{ marginLeft: 'auto', gap: 12 }}>
          {currentRepoId && repoMetaQuery.data && <RoleBadge role={repoMetaQuery.data.role} />}
          {session && (
            <div className="row" style={{ gap: 4 }}>
              <ThemeToggle />
              <WhatsNewTrigger />
            </div>
          )}
          {session && (
            <UserMenu
              name={session.name}
              email={session.email}
              avatarUrl={session.avatarUrl}
              currentRepoId={currentRepoId}
              onLogout={async () => {
                // Doit passer AVANT logout() : /users/me/preferences exige une session valide,
                // que logout() efface justement côté serveur. Se déconnecter depuis la liste
                // "nue" (pas un repo précis) doit y ramener à la prochaine connexion plutôt que
                // de rejouer la redirection vers le dernier repo réellement ouvert — sans effet
                // si la déconnexion se fait depuis /repos/:id, où lastRepoId reste pertinent.
                if (location.pathname === '/repos') {
                  try {
                    const updated = await api.updatePreferences({ lastRepoId: null });
                    queryClient.setQueryData(['preferences'], updated);
                  } catch (err) {
                    console.error('Échec de la remise à zéro du dernier repo ouvert au logout', err);
                  }
                }
                try {
                  await api.logout();
                } catch (err) {
                  // Le state client se nettoie quand même, mais si le cookie serveur n'a pas
                  // pu être effacé, la session reviendra au prochain reload — visible ici
                  // plutôt que silencieusement avalé.
                  console.error('Logout serveur a échoué, session probablement toujours active côté API', err);
                }
                flushSync(() => { queryClient.setQueryData(['session'], null); });
                // AppInner (donc ce ref) survit au logout — sans ce reset, une reconnexion dans
                // le même onglet (sans reload) ne redéclencherait jamais la redirection vers le
                // dernier repo ouvert, alors que la préférence est toujours là.
                didAutoNavigateToLastRepo.current = false;
                navigate('/login', { replace: true });
              }}
            />
          )}
        </div>
      </header>
      <main className="main">
        <Outlet />
      </main>
    </div>
  );
}

export function App() {
  return (
    <SidebarProvider>
      <AppInner />
    </SidebarProvider>
  );
}
